import type { UserRole } from '@/types/icrop';

/**
 * Role ranking, most → least privileged. A user "meets" a requirement when
 * their rank is at least as high as the requirement (i.e. their index is <=
 * the requirement's index).
 *
 * Single source of truth shared by ProtectedRoute (route gating) and the
 * Layout nav (menu visibility) so the menu can never surface a destination the
 * route would reject — the rule is "hide what you can't reach".
 */
export const ROLE_HIERARCHY: UserRole[] = [
  'super_admin',
  'account_admin',
  'manager',
  'user',
  'viewer',
];

/** True when `userRole` is at least as privileged as `requiredRole`. */
export function roleMeetsRequirement(
  userRole: UserRole | null | undefined,
  requiredRole: UserRole,
): boolean {
  if (!userRole) return false;
  const userIdx = ROLE_HIERARCHY.indexOf(userRole);
  const reqIdx = ROLE_HIERARCHY.indexOf(requiredRole);
  return userIdx !== -1 && reqIdx !== -1 && userIdx <= reqIdx;
}

export interface RoleOption {
  value: UserRole;
  label: string;
  description: string;
}

/**
 * Roles an account admin can assign from the user-facing forms. Single source
 * of truth — previously duplicated across UserEditModal, OrganizationDetail and
 * UserInviteModal. The descriptions are the superset wording (formerly the
 * invite modal's): they name everything the role can touch, not just documents.
 * super_admin is deliberately NOT here: it is granted only from the iCrop
 * console, and the edit forms must never offer it (or silently demote away
 * from it via a blank controlled select).
 */
export const ROLE_OPTIONS: RoleOption[] = [
  { value: 'viewer', label: 'Viewer', description: 'Can view documents and organizations (read-only)' },
  { value: 'user', label: 'User', description: 'Can upload documents and manage basic data' },
  { value: 'manager', label: 'Manager', description: 'Can manage organizations, documents, and users' },
  { value: 'account_admin', label: 'Account Admin', description: 'Full access to all organizations' },
];

/**
 * Shown (read-only) when the row being edited is a super_admin, so the
 * controlled select renders a real option instead of a blank value.
 */
export const SUPER_ADMIN_OPTION: RoleOption = {
  value: 'super_admin',
  label: 'Super Admin',
  description: 'iCrop administrator — this role can only be changed from the iCrop console',
};

/**
 * Badge colors for role pills. Single source of truth — previously duplicated
 * across UserManagement and OrganizationDetail.
 */
export function getRoleColor(role: UserRole): string {
  switch (role) {
    case 'super_admin': return 'bg-purple-100 text-purple-800';
    case 'account_admin': return 'bg-red-100 text-red-800';
    case 'manager': return 'bg-blue-100 text-blue-800';
    case 'user': return 'bg-green-100 text-green-800';
    case 'viewer': return 'bg-gray-100 text-gray-800';
    default: return 'bg-gray-100 text-gray-800';
  }
}
